import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { setTokenGetter } from "./tokenGetter";

interface TokenContextType {
  token: string | null;
  setToken: (t: string | null) => void;
  logout: () => void;
  clearTokenAndUser: () => void;
}

export const TokenContext = createContext<TokenContextType | undefined>(
  undefined,
);

const TOKEN_COOKIE_KEY = "accessToken";

const getCookieToken = (): string | null => {
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${TOKEN_COOKIE_KEY}=`));
  return match ? decodeURIComponent(match.split("=")[1]) : null;
};

const setCookieToken = (token: string) => {
  document.cookie = `${TOKEN_COOKIE_KEY}=${encodeURIComponent(token)}; path=/; SameSite=Strict; Secure`;
};

const clearCookieToken = () => {
  document.cookie = `${TOKEN_COOKIE_KEY}=; path=/; expires=${new Date(0).toUTCString()}; SameSite=Strict; Secure`;
};

export const TokenProvider = ({ children }: { children: ReactNode }) => {
  const initialToken = getCookieToken();

  setTokenGetter(() => initialToken);

  const [token, setTokenState] = useState(initialToken);

  useEffect(() => {
    setTokenGetter(() => token);
  }, [token]);

  const setToken = useCallback((newToken: string | null) => {
    if (newToken) {
      setCookieToken(newToken);
    } else {
      clearCookieToken();
    }
    setTokenState(newToken);
  }, []);

  const clearTokenAndUser = useCallback(() => {
    clearCookieToken();
    setTokenState(null);
  }, []);

  const logout = useCallback(() => {
    clearCookieToken();
    setTokenState(null);
  }, []);

  const value = useMemo(
    () => ({ token, setToken, logout, clearTokenAndUser }),
    [token, setToken, logout, clearTokenAndUser],
  );

  return (
    <TokenContext.Provider value={value}>{children}</TokenContext.Provider>
  );
};

export const useToken = (): TokenContextType => {
  const context = useContext(TokenContext);
  if (!context) throw new Error("useToken must be used inside TokenProvider");
  return context;
};
